import { useState } from 'react';

import { GapDraftEditableKind } from '@momentum/types';

import type { EditableGapFieldState } from '../lib/draft-edit';

interface EditableFieldProps {
  field: EditableGapFieldState;
  onValueChange: (value: string) => void;
  onReasonChange: (reason: string) => void;
}

/**
 * One field of the edit form: the control, what the field is, and — folded
 * away until asked for — why the change was made.
 *
 * The reason is per change rather than per save because a pass over a draft is
 * rarely one decision: a reviewer fixing a title and rewriting the impact
 * paragraph did those for different reasons, and one box at the bottom would
 * flatten both into whichever they typed. It stays collapsed because the
 * reasons are for improving the prompts later, not for the reviewer's own
 * bookkeeping — asking for one every time would tax the edit into not
 * happening.
 *
 * Every field here holds a final value, so the control always opens on
 * something: the form offers only what the pipeline actually produced.
 */
export function EditableField({
  field,
  onValueChange,
  onReasonChange,
}: EditableFieldProps) {
  const [isReasonOpen, setIsReasonOpen] = useState(false);
  const hasReason = field.reason.trim().length > 0;

  return (
    <div className="gr-edit-field" data-dirty={field.isDirty || undefined}>
      <div className="gr-edit-field-head">
        <span className="gr-edit-label">{field.label}</span>
        {field.wasEdited && <span className="gr-edit-tag">edited before</span>}
        {field.isDirty && (
          <span className="gr-edit-tag" data-tone="cyan">
            changed
          </span>
        )}
        <code className="gr-edit-path">{field.path}</code>
      </div>

      {field.kind === GapDraftEditableKind.MARKDOWN && (
        <textarea
          className="gr-edit-input"
          value={field.value}
          rows={4}
          placeholder="Cleared — saving empties this field."
          onChange={(changeEvent) => onValueChange(changeEvent.target.value)}
        />
      )}
      {field.kind === GapDraftEditableKind.TEXT && (
        <input
          className="gr-edit-input"
          type="text"
          value={field.value}
          placeholder="Cleared — saving empties this field."
          onChange={(changeEvent) => onValueChange(changeEvent.target.value)}
        />
      )}
      {field.kind === GapDraftEditableKind.SCORE && (
        <input
          className="gr-edit-input gr-edit-score"
          type="number"
          min={0}
          max={1}
          step={0.05}
          value={field.value}
          placeholder="0.00"
          onChange={(changeEvent) => onValueChange(changeEvent.target.value)}
        />
      )}

      {field.problem !== undefined && (
        <p className="gr-edit-problem">{field.problem}</p>
      )}

      <div className="gr-edit-why">
        {isReasonOpen || hasReason ? (
          <input
            className="gr-edit-input gr-edit-reason"
            type="text"
            value={field.reason}
            placeholder="Why this change? — optional, and only read when the prompts are revised"
            onChange={(changeEvent) => onReasonChange(changeEvent.target.value)}
          />
        ) : (
          <button
            className="gr-edit-why-open"
            type="button"
            onClick={() => setIsReasonOpen(true)}
          >
            why?
          </button>
        )}
      </div>
    </div>
  );
}
